import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  untracked,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AlertService,
  ButtonLoadingDirective,
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  IconComponent,
} from '@gains-com/ui-angular';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { LOGGER_FACTORY } from '../../core/logger/logger-factory.provider.js';
import { environment } from '../../../environments/environment.js';
import { LottieAnimationComponent } from '../lottie-animation/lottie-animation.component.js';
import { DocumentVerificationService } from '../../core/document-verification.service.js';
import { DocumentVerificationViewService } from '../../core/view-states/document-verification-view.service.js';
import { UserViewService } from '../../core/view-states/user-view.service.js';
import {
  CasinoSettingKeyId,
  TargetEntityQuery,
  DocumentVerificationStatus as DocVerStatus,
  AnalyticsEvent,
} from '@gains-com/shared';
import { MetadataViewService } from '../../core/view-states/metadata-view.service.js';
import { PaymentMethodsViewService } from '../../core/view-states/payment-methods-view.service.js';
import { PaymentMethodUpdateRTM } from '@gains-com/realtime';
import { EnvironmentPipe } from '../../pipes/environment.pipe.js';
import { AuthService } from '../../core/auth.service.js';

import type {
  DocumentVerificationDialogData,
  DocumentVerificationDialogResult,
  DocumentVerificationStatus,
} from './document-verification-dialog.types.js';
import {
  deriveStatusFromVerifications,
  getVerificationsForTargetEntityQuery,
  makeTargetEntity,
  makeTargetEntityQuery,
  getCreditCardPaymentMethodsForDisplay,
} from './document-verification-dialog.utils.js';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { Analytics } from '../../core/analytics/analytics.service.js';
import { isPlatformBrowser } from '@angular/common';

declare const Persona: any;

@Component({
  selector: 'app-document-verification-dialog',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardContentComponent,
    IconComponent,
    MatButton,
    MatIconButton,
    MatCheckbox,
    ButtonLoadingDirective,
    LottieAnimationComponent,
    EnvironmentPipe,
  ],
  templateUrl: './document-verification-dialog.component.html',
  styleUrl: './document-verification-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentVerificationDialogComponent implements OnInit, OnDestroy {
  private static readonly PENDING_TIMEOUT_MS = 120_000; // 2 minutes
  private _pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private _verificationResponseTimeout: ReturnType<typeof setTimeout> | null = null;
  private _statusUpdateDebounce: ReturnType<typeof setTimeout> | null = null;
  private _clientMap: Record<string, any> = {};
  private _blockedEventTracked = false;

  private readonly _dialogRef = inject(DialogRef<DocumentVerificationDialogResult>);
  private readonly _data = inject<DocumentVerificationDialogData | null>(DIALOG_DATA, { optional: true });
  private readonly _docVerificationService = inject(DocumentVerificationService);
  private readonly _docVerificationViewService = inject(DocumentVerificationViewService);
  private readonly _userView = inject(UserViewService);
  private readonly _alert = inject(AlertService);
  private readonly _logger = inject(LOGGER_FACTORY).create('DocumentVerificationDialog');
  private readonly _analytics = inject(Analytics);
  private readonly _metadataView = inject(MetadataViewService);
  private readonly _paymentMethodsView = inject(PaymentMethodsViewService);
  private readonly _auth = inject(AuthService);
  private readonly _platformId = inject(PLATFORM_ID);

  private readonly _verifications = toSignal(this._docVerificationViewService.documentVerifications$);
  private readonly _paymentMethods = toSignal(this._paymentMethodsView.paymentMethods$, {
    initialValue: null as PaymentMethodUpdateRTM[] | null,
  });

  userView = toSignal(this._userView.state$);

  autoClose = signal(this._data?.autoClose ?? false);
  inquiryCreated = signal(false);
  inquiryCreateInProgress = signal(false);
  initialLoaded = signal(false);
  initialVerificationStatuses = signal(new Map<string, DocVerStatus>());
  noInquiryResponse = signal(false);
  status = signal<DocumentVerificationStatus>('pending');

  acknowledged = signal(false);
  viewMode = signal<'verify' | 'example'>('verify');

  private _targetEntityQuery = computed(() => this._makeTargetEntityQuery());

  startVerificationLoading = computed(() => this.inquiryCreateInProgress() || !this.initialLoaded());

  startVerificationDisabled = computed(
    () => !this.initialLoaded() || this.inquiryCreateInProgress() || !this.acknowledged(),
  );

  verifications = computed(() => {
    return getVerificationsForTargetEntityQuery(this._targetEntityQuery(), this._verifications() ?? []);
  });

  isBlocked = computed(() => this.noInquiryResponse() || this.status() === 'blocked');

  personaDocFeatureEnabled = computed(() => this.userView()?.persona_document_verification_enabled === true);

  creditCardPaymentMethods = computed(() => {
    const targetEntityTypeIds = this._normalizeToArray(this._data?.targetEntity?.targetEntityTypeId);
    return getCreditCardPaymentMethodsForDisplay(this._paymentMethods(), targetEntityTypeIds);
  });

  constructor() {
    effect(() => {
      let verifications = this.verifications();

      if (deriveStatusFromVerifications(verifications) === 'approved') {
        this._handleApproved();
        return;
      }

      const personaDocFeatureEnabled = this.personaDocFeatureEnabled();

      const initialStatuses = untracked(this.initialVerificationStatuses);
      verifications = verifications.filter((v) => v.status !== initialStatuses.get(v.document_verification_id));

      const verificationStatus = deriveStatusFromVerifications(verifications);

      this.initialVerificationStatuses.set(
        new Map(
          Object.entries(initialStatuses).filter(
            ([id]) => !verifications.some((v) => v.document_verification_id === id),
          ),
        ),
      );

      if (this._statusUpdateDebounce) {
        clearTimeout(this._statusUpdateDebounce);
      }

      if (verifications.length && this._verificationResponseTimeout) {
        clearTimeout(this._verificationResponseTimeout);
      }

      const newStatus =
        verificationStatus !== 'pending' && verificationStatus !== 'approved' && !personaDocFeatureEnabled
          ? 'blocked'
          : verificationStatus;

      // Clear pending timer if status is no longer pending (real-time update arrived)
      if (newStatus !== 'pending') {
        untracked(() => this._clearPendingTimer());
      }

      if (newStatus === 'rejected' || newStatus === 'blocked') {
        this._statusUpdateDebounce = setTimeout(() => this.status.set(newStatus), 1000);
      } else {
        this.status.set(newStatus);
      }
    });

    effect(() => {
      if (this.isBlocked() && !this._blockedEventTracked) {
        this._blockedEventTracked = true;
        this._analytics.trackEvent(AnalyticsEvent.PersonaDocsBlocked, {});
      }
    });
  }

  async ngOnInit() {
    // TODO: Remove this when we figure out the Angular Material popover issue
    // https://github.com/angular/components/issues/31504
    try {
      if (isPlatformBrowser(this._platformId)) {
        Array.from(document.querySelectorAll('[popover]')).forEach((e) => e.removeAttribute('popover'));
      }
    } catch (err) {
      this._logger.error('Error removing popover', err);
    }

    try {
      await Promise.all([
        this._docVerificationViewService.syncDocumentVerifications(),
        this._userView.syncUserFeatures(),
      ]);

      // `stateP` resolves null when `/self` fails (it keeps retrying in the
      // background, but this dialog reads a single value). Treat unknown as
      // pending, which is where the thrown dereference used to land anyway —
      // only `persona_document_verification_enabled === false` blocks.
      const userState = await this._userView.stateP;

      this.status.set(userState?.persona_document_verification_enabled === false ? 'blocked' : 'pending');
    } catch (err: any) {
      this._logger.error(`Document verification initialization error: ${err.message}`, { error: err });
    } finally {
      this.initialLoaded.set(true);
    }
  }

  ngOnDestroy() {
    this._clearPendingTimer();

    if (this._verificationResponseTimeout) {
      clearTimeout(this._verificationResponseTimeout);
    }

    if (this._statusUpdateDebounce) {
      clearTimeout(this._statusUpdateDebounce);
    }

    // Clean up Persona clients
    Object.keys(this._clientMap).forEach((key) => {
      try {
        this._clientMap[key]?.destroy?.();
      } catch {
        // Ignore cleanup errors
      }
    });
    this._clientMap = {};
  }

  async retryVerification() {
    this._resetState();

    this.noInquiryResponse.set(false);
    this.inquiryCreateInProgress.set(false);

    return this.startVerification();
  }

  onContinue() {
    this._dialogRef.close('verified');
  }

  showExample() {
    this.viewMode.set('example');
    this._analytics.trackEvent(AnalyticsEvent.PaymentMethodVerificationStatementExampleViewed, {});
  }

  hideExample() {
    this.viewMode.set('verify');
  }

  onVerifyLater() {
    if (this.isBlocked()) {
      // User explicitly chose to proceed with held redemption
      this._dialogRef.close('blocked');
    } else {
      this._dialogRef.close('cancelled');
    }
  }

  close(source?: 'header' | 'button') {
    const status = this.status();
    if (status === 'approved') {
      this._dialogRef.close('verified');
    } else if (status === 'pending') {
      this._dialogRef.close('pending');
    } else if (status === 'rejected') {
      this._dialogRef.close('rejected');
    } else if (this.isBlocked() && source !== 'header') {
      this._dialogRef.close('blocked');
    } else {
      this._dialogRef.close('cancelled');
    }
  }

  async startVerification() {
    try {
      this.inquiryCreated.set(false);
      this.inquiryCreateInProgress.set(true);

      this.initialVerificationStatuses.set(
        new Map(
          (await lastValueFrom(this._docVerificationViewService.documentVerifications$.pipe(take(1)))).map(
            (dv) => [dv.document_verification_id, dv.status] as const,
          ),
        ),
      );

      const targetEntityQuery = this._makeTargetEntityQuery();
      const targetEntity = makeTargetEntity(targetEntityQuery);

      const response = await this._docVerificationService.createDocumentVerificationPersonaDocumentVerification(
        targetEntity ?? undefined,
        !targetEntity && targetEntityQuery ? targetEntityQuery : undefined,
      );

      if (!response.persona) {
        this.inquiryCreateInProgress.set(false);
        this.noInquiryResponse.set(true);
        return;
      }

      this._analytics.trackEvent(AnalyticsEvent.PaymentMethodVerificationStarted, {
        inquiry_id: response.persona.inquiry_id,
      });

      this._createClient(response.persona.inquiry_id).open();
    } catch (err: any) {
      this._logger.error(`Document verification startVerification() error: ${err.message}`, { error: err });
      this.inquiryCreateInProgress.set(false);
    }
  }

  private async _handleApproved() {
    this._clearPendingTimer();

    if (this._statusUpdateDebounce) {
      clearTimeout(this._statusUpdateDebounce);
    }

    if (this._verificationResponseTimeout) {
      clearTimeout(this._verificationResponseTimeout);
    }

    try {
      await this._auth.forceUserReload();
    } catch (err: any) {
      this._logger.error('Failed to refresh token after doc verification', {
        error: err.message,
      });
    }

    if (this.autoClose()) {
      this._dialogRef.close('verified');
    } else {
      this.status.set('approved');
    }
  }

  private _createClient(inquiryId: string) {
    if (this._clientMap[inquiryId]) {
      return this._clientMap[inquiryId];
    }

    return (this._clientMap[inquiryId] = new Persona.Client({
      inquiryId,
      frameAncestors: [environment.webUrl],
      messageTargetOrigin: environment.webUrl,
      environment:
        environment.env === 'local' ? 'sandbox' : (environment.persona.environmentId as 'sandbox' | 'production'),
      onComplete: async () => {
        this.inquiryCreateInProgress.set(false);
        this.inquiryCreated.set(true);
        this.status.set('pending');

        // Start 2-minute pending timer (backend reconcile runs every 10 min)
        this._startPendingTimer();

        if (this._verificationResponseTimeout) {
          clearTimeout(this._verificationResponseTimeout);
        }

        this._verificationResponseTimeout = setTimeout(() => {
          this._alert.notifyDanger(
            'Document verification error',
            'There was an error verifying your documents. Please try again.',
          );

          this._resetState();
        }, 15e3);

        return this._docVerificationViewService.syncDocumentVerifications();
      },
      onCancel: () => {
        this._logger.info('Persona document verification cancelled', {
          inquiryId,
        });
        this._resetState();
      },
      onError: (error: any) => {
        this._logger.error(`Document verification error: ${error.message}`, {
          error,
        });
        this._alert.notifyDanger(
          'Document verification error',
          'There was an error verifying your documents. Please try again.',
        );

        this._resetState();
      },
    }));
  }

  /**
   * Starts a 2-minute timer that will change status to 'blocked' if
   * still pending after inquiry creation. This handles the case where
   * backend evaluation is delayed due to Redis lock contention.
   * The backend reconcile job runs every 10 minutes, so we give ample
   * time for the real-time update to arrive before showing blocked status.
   */
  private _startPendingTimer() {
    this._clearPendingTimer();

    this._pendingTimer = setTimeout(() => {
      if (this.status() === 'pending') {
        this._logger.info('Pending timer expired, changing status to blocked');
        this.status.set('blocked');
      }
    }, DocumentVerificationDialogComponent.PENDING_TIMEOUT_MS);
  }

  private _clearPendingTimer() {
    if (this._pendingTimer) {
      clearTimeout(this._pendingTimer);
      this._pendingTimer = null;
    }
  }

  private _resetState() {
    this._clearPendingTimer();
    this.noInquiryResponse.set(false);
    this.inquiryCreateInProgress.set(false);
    this.inquiryCreated.set(false);
    this.initialVerificationStatuses.set(new Map<string, DocVerStatus>());
    this.viewMode.set('verify');
    this.acknowledged.set(false);

    if (this._verificationResponseTimeout) {
      clearTimeout(this._verificationResponseTimeout);
    }
  }

  private _makeTargetEntityQuery(): TargetEntityQuery | null {
    const targetEntity = this._data?.targetEntity;
    if (!targetEntity) return null;

    return makeTargetEntityQuery({
      targetEntityTypeId: targetEntity.targetEntityTypeId,
      targetEntityId: targetEntity.targetEntityId,
      documentVerificationTypeId: targetEntity.documentVerificationTypeId,
    });
  }

  private _normalizeToArray<T>(value: T | T[] | undefined): T[] {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }
}
